import { useState, useEffect } from "react";
import { collection, query, orderBy, onSnapshot, doc, setDoc, deleteDoc, writeBatch, addDoc, serverTimestamp, getDocs } from "firebase/firestore";
import { db } from "services/firebaseConfig";
import toast from "react-hot-toast";

export const useFinancialHistory = () => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(
      collection(db, "financial_history"),
      orderBy("date", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setHistory(data);
        setIsLoading(false);
      },
      (err) => {
        console.error("Erro ao buscar histórico financeiro:", err);
        setError("Não foi possível carregar o histórico.");
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const saveDayRecord = async (dateStr, data) => {
    try {
      const docRef = doc(db, "financial_history", dateStr);
      await setDoc(docRef, data, { merge: true });
      toast.success("Registro salvo com sucesso!");
      return true;
    } catch (err) {
      console.error("Erro ao salvar registro:", err);
      toast.error("Erro ao salvar registro.");
      return false;
    }
  };

  const deleteDayRecord = async (dateStr) => {
    try {
      const docRef = doc(db, "financial_history", dateStr);
      await deleteDoc(docRef);
      toast.success("Registro excluído.");
      return true;
    } catch (err) {
      console.error("Erro ao excluir registro:", err);
      toast.error("Erro ao excluir.");
      return false;
    }
  };

  const closeSeason = async (seasonName) => {
    if (!seasonName.trim()) return false;
    try {
      const batch = writeBatch(db);
      
      // Salva o retrato na coleção financial_seasons
      const seasonsRef = collection(db, "financial_seasons");
      await addDoc(seasonsRef, {
        nome: seasonName,
        dataFechamento: serverTimestamp(),
        records: history
      });

      // Deleta os registros atuais
      history.forEach((record) => {
        const docRef = doc(db, "financial_history", record.id);
        batch.delete(docRef);
      });
      await batch.commit();
      
      toast.success("Temporada financeira fechada e zerada com sucesso!");
      return true;
    } catch (err) {
      console.error("Erro ao fechar temporada:", err);
      toast.error("Erro ao fechar temporada.");
      return false;
    }
  };

  const fetchSeasons = async () => {
    try {
      const q = query(collection(db, "financial_seasons"), orderBy("dataFechamento", "desc"));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (err) {
      console.error("Erro ao buscar temporadas:", err);
      return [];
    }
  };

  const importCsvBatch = async (dataArray) => {
    try {
      let imported = 0;
      let batch = writeBatch(db);
      let opCount = 0;

      for (let i = 0; i < dataArray.length; i++) {
        const data = dataArray[i];
        
        const docRef = doc(db, "financial_history", data.date);
        batch.set(docRef, data, { merge: true });
        imported++;
        opCount++;

        // Firestore suporta até 500 ops por batch
        if (opCount === 400) {
          await batch.commit();
          batch = writeBatch(db);
          opCount = 0;
        }
      }

      if (opCount > 0) {
        await batch.commit();
      }

      toast.success(`${imported} registros processados e importados com sucesso!`);
      return true;
    } catch (err) {
      console.error("Erro ao importar CSV em lote:", err);
      toast.error("Erro ao importar CSV.");
      return false;
    }
  };

  return {
    history,
    isLoading,
    error,
    saveDayRecord,
    deleteDayRecord,
    closeSeason,
    fetchSeasons,
    importCsvBatch
  };
};
